import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { Select } from "../../../components/Select";
import { localizedName } from "../../../lib/localizedName";
import { formatDate, formatDateTime } from "../../../lib/format";
import Ic from "../../../components/icons.jsx";
import type { ApiError } from "../../../api/client";
import {
  qualityReadingApi,
  testPlanApi,
  type CoverageView,
  type QualityPhase,
  type QualityReading,
  type TestPlan,
} from "../../../api/quality";
import { traceabilityApi, type LotHit } from "../../../api/traceability";
import { ExternalAnalysesPanel } from "../../../components/ExternalAnalyses";
import { useAuth } from "../../../auth/AuthContext";
import { labArchiveApi, type BatchArchiveContext } from "../../../api/labArchive";
import { frequencyLabel, phaseHint, phaseLabel } from "./QualityPhaseLabels";
import { missingPointLabel } from "./archivePointLabel";

/** Faze koje se unose ovde. Prijem nije među njima — on ispravlja novac i stoji na isporuci. */
const FAZE: QualityPhase[] = ["SEMI_FINISHED", "FINISHED", "DISPATCH"];

/**
 * Unos nalaza na LOT-u i pokrivenost plana.
 *
 * Konfigurator kaže šta se meri; ovde se to meri. Ekran radi od LOT-a jer je to ono što laborant ima u
 * ruci, i spaja dve stvari koje bez druge ne znače ništa: izmerene vrednosti, i koliko je od propisanog
 * zaista izvedeno.
 *
 * Tri stvari ekran kaže naglas: vrednost koja je izašla iz prijemne granice čeka potpis šefa
 * laboratorije (KK29), propisana stavka bez unete granice se ne može oceniti, a stavka koja se meri po
 * paleti se ne može oceniti bez broja paleta.
 */
const FAZE_SA_ADRESE = ["INTAKE", "PROCESS", "SEMI_FINISHED", "FINISHED", "DISPATCH"] as const;

export default function QualityLotReadingsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";

  // Ekran ima dva citaoca. Laboratorija meri i upisuje; direktor prodaje cita - nalaz, propisani
  // plan i pokrivenost su tri stvari od kojih sastavlja odgovor kupcu na reklamaciju. Zato se
  // sakriva samo dugme za unos, a izbor plana i pokrivenost ostaju. Bez ove razlike ekran je
  // trazio plan odmah po ucitavanju i direktoru prodaje vracao Access denied na ekranu koji je
  // njegov.
  const smeDaUnosi = hasPermission("quality_readings:record");
  const smeDaVidiPlan = hasPermission("test_plans:view");
  // Potpis na pokrivenost drzi sef laboratorije, istim kljucem kojim potpisuje i nalaz van granice
  // (KK29). Kljuc kojim se plan pise namerno nije ovaj: ko propisuje test nije time i onaj ko
  // potvrdjuje da je izveden.
  const smeDaPotpise = hasPermission("quality_readings:verify");

  const [term, setTerm] = useState("");
  const [hits, setHits] = useState<LotHit[] | null>(null);
  // Odsečen spisak mora da kaže da je odsečen: tih spisak od pedeset redova čitalac čita kao
  // „toliko ih ima" i dalje traži po onome što je već odsečeno (pravilo 15).
  const [odseceno, setOdseceno] = useState(0);
  const [lot, setLot] = useState<LotHit | null>(null);
  const [readings, setReadings] = useState<QualityReading[] | null>(null);
  const [plans, setPlans] = useState<TestPlan[] | null>(null);
  const [planId, setPlanId] = useState("");
  const [phase, setPhase] = useState<QualityPhase>("FINISHED");
  const [pallets, setPallets] = useState("");
  const [coverage, setCoverage] = useState<CoverageView | null>(null);
  const [adding, setAdding] = useState(false);
  const [arhiv, setArhiv] = useState<BatchArchiveContext | null>(null);

  useEffect(() => {
    if (!smeDaVidiPlan) {
      setPlans([]);
      return;
    }
    void testPlanApi
      .list()
      .then((rows) => {
        setPlans(rows);
        const prvi = rows.find((p) => !p.culture) ?? rows[0];
        if (prvi) setPlanId(prvi.id);
      })
      .catch((e: ApiError) => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smeDaVidiPlan]);

  const loadLot = useCallback(
    async (hit: LotHit) => {
      setLot(hit);
      setHits(null);
      setArhiv(null);
      try {
        setReadings(await qualityReadingApi.forLot(hit.id));
      } catch (e) {
        toast.error((e as ApiError).message);
      }
      // Arhiv se ucitava odvojeno: nalaz iz starog programa nije portalski nalaz, pa ne sme da
      // padne zajedno sa njim niti da ga odlozi.
      try {
        setArhiv(await labArchiveApi.batchContext(hit.id));
      } catch {
        setArhiv(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadCoverage = useCallback(async () => {
    if (!lot || !planId || !smeDaVidiPlan) return;
    try {
      setCoverage(
        await qualityReadingApi.coverage(lot.id, planId, phase, pallets.trim() ? Number(pallets) : null),
      );
    } catch (e) {
      toast.error((e as ApiError).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lot, planId, phase, pallets, smeDaVidiPlan]);

  // Dolazak sa radnog reda laboratorije: red tamo nosi oznaku šarže, fazu i plan, a ovaj ekran se
  // ponaša kao da je laborant to ukucao. Bez ovoga bi veza obećavala šaržu koju odredište ignoriše.
  //
  // Traži se po oznaci, a bira se po IDENTITETU: pretraga po oznaci na soji vraća i tuđe šarže, jer
  // `designation` tamo nosi klasu. Kad se traženi id ne nađe među pogocima, pogoci ostaju na ekranu
  // da laborant izabere — ne bira se prvi.
  const [izAdrese, setIzAdrese] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const oznaka = p.get("q");
    const id = p.get("lot");
    return oznaka && id ? { oznaka, id, faza: p.get("phase"), plan: p.get("plan") } : null;
  });

  useEffect(() => {
    if (!izAdrese) return;
    setIzAdrese(null);
    if (izAdrese.faza && (FAZE_SA_ADRESE as readonly string[]).includes(izAdrese.faza)) {
      setPhase(izAdrese.faza as QualityPhase);
    }
    setTerm(izAdrese.oznaka);
    void (async () => {
      try {
        const nadjeno = await traceabilityApi.search(izAdrese.oznaka);
        const tacan = nadjeno.rows.find((r) => r.id === izAdrese.id);
        if (tacan) {
          await loadLot(tacan);
        } else {
          setHits(nadjeno.rows);
          setOdseceno(nadjeno.cut ? nadjeno.totalAtMost : 0);
        }
      } catch (e) {
        toast.error((e as ApiError).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [izAdrese]);

  // Plan sa adrese se primenjuje tek kad planovi stignu, jer ih učitava drugi efekt.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("plan");
    if (!p || !plans) return;
    if (plans.some((x) => x.id === p)) setPlanId(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  useEffect(() => {
    void loadCoverage();
  }, [loadCoverage]);

  async function search() {
    if (term.trim().length < 2) return;
    try {
      const nadjeno = await traceabilityApi.search(term.trim());
      setHits(nadjeno.rows);
      setOdseceno(nadjeno.cut ? nadjeno.totalAtMost : 0);
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  async function refresh() {
    if (!lot) return;
    setReadings(await qualityReadingApi.forLot(lot.id));
    await loadCoverage();
  }

  const otvoreniPlan = (plans ?? []).find((p) => p.id === planId) ?? null;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("readings.title", "Nalazi na LOT-u")}</h1>
          <div className="sub">
            {t(
              "readings.lead",
              "Konfigurator kaže šta se meri; ovde se upisuje izmereno i vidi koliko je od propisanog izvedeno.",
            )}
          </div>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 0 }}>
        <input
          className="input"
          style={{ maxWidth: 320 }}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void search();
          }}
          placeholder={t("readings.search", "Oznaka LOT-a, npr. 150926E4A18542")}
        />
        <button className="btn" type="button" onClick={() => void search()}>
          <Ic.search /> {t("common.search", "Traži")}
        </button>
      </div>

      {hits && hits.length === 0 && (
        <div className="card">
          <div className="empty">{t("dispatchRelease.noHits", "Nema LOT-a sa tom oznakom.")}</div>
        </div>
      )}
      {hits && hits.length > 0 && (
        <div className="card">
          {odseceno > 0 && (
            <div className="sub" style={{ padding: "6px 12px" }}>
              {t("lotSearch.cut", "Prikazano prvih {{n}} od {{svih}} šarži — suzite pretragu.", {
                n: hits.length, svih: odseceno,
              })}
            </div>
          )}
          <table className="tbl">
            <tbody>
            {hits.map((h) => (
              <tr key={h.id}>
                {/* Oznaka i barkod stoje oba: na soji iz ERP-a oznaka nosi klasu, a ono po čemu
                    je čovek i tražio je barkod — ko je ukucao, treba i da vidi šta je pogodio. */}
                <td style={{ fontFamily: "var(--mono)" }}>{h.barcode ?? h.designation ?? "—"}</td>
                <td style={tiho}>{h.productName ?? "—"}</td>
                <td style={tiho}>{h.producedOn ? formatDate(h.producedOn) : "—"}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn sm" type="button" onClick={() => void loadLot(h)}>
                    {t("readings.open", "Otvori")}
                  </button>
                </td>
              </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!lot ? (
        <div className="card">
          <div className="empty">{t("readings.pickLot", "Nađite LOT po oznaci da bi se videli nalazi.")}</div>
        </div>
      ) : (
        <>
          <div className="card">
            <header className="card-h">
              <div>
                <h3 style={{ fontFamily: "var(--mono)" }}>{lot.designation}</h3>
                <div className="sub">
                  {lot.productName ?? "—"}
                  {lot.producedOn
                    ? ` · ${t("dispatchRelease.producedOn", "proizveden {{datum}}", { datum: formatDate(lot.producedOn) })}`
                    : ""}
                </div>
              </div>
            </header>
            {smeDaVidiPlan && (
            <div className="card-b" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ ...tiho, marginBottom: 4 }}>{t("readings.plan", "Plan kontrolisanja")}</div>
              <Select
                value={planId}
                onChange={setPlanId}
                options={(plans ?? []).map((p) => ({
                  value: p.id,
                  label: localizedName({ name: p.code, nameI18n: p.nameI18n }, lang),
                }))}
              />
            </div>
            <div>
              <div style={{ ...tiho, marginBottom: 4 }}>{t("readings.phase", "Faza")}</div>
              <Select
                value={phase}
                onChange={(v) => setPhase(v as QualityPhase)}
                options={FAZE.map((f) => ({ value: f, label: phaseLabel(t, f) }))}
              />
            </div>
            <div>
              <div style={{ ...tiho, marginBottom: 4 }}>{t("readings.pallets", "Broj paleta")}</div>
              <input
                className="input"
                style={{ maxWidth: 110 }}
                value={pallets}
                onChange={(e) => setPallets(e.target.value)}
              />
            </div>
              {smeDaUnosi ? (
                <button className="btn primary" type="button" onClick={() => setAdding(true)}>
                  <Ic.plus /> {t("readings.add", "Upiši nalaz")}
                </button>
              ) : (
                <span className="muted" style={{ fontSize: 12 }}>
                  {t("readings.viewOnly", "Nalaze upisuje kontrola kvaliteta; vi ih ovde čitate.")}
                </span>
              )}
              <div style={{ ...tiho, flexBasis: "100%" }}>{phaseHint(t, phase)}</div>
            </div>
            )}
          </div>

          {adding && otvoreniPlan && (
            <ReadingForm
              lotId={lot.id}
              phase={phase}
              plan={otvoreniPlan}
              lang={lang}
              onDone={async () => {
                setAdding(false);
                await refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          )}

          {smeDaVidiPlan && coverage && (
            <CoverageTable
              view={coverage}
              lang={lang}
              lotId={lot.id}
              canSign={smeDaPotpise}
              onSigned={loadCoverage}
            />
          )}

          <div className="card">
            <header className="card-h">
              <h3>{t("readings.recorded", "Upisani nalazi")}</h3>
            </header>
            {!readings ? (
              <div className="card-b">
                <PanelLoading text={t("common.loading", "Učitavanje…")} />
              </div>
            ) : readings.length === 0 ? (
              <div className="empty">{t("readings.noneYet", "Za ovaj LOT još nema nalaza.")}</div>
            ) : (
              <div className="card-b tight">
                <ReadingTable readings={readings} lang={lang} onChanged={refresh} canSign={smeDaPotpise} />
              </div>
            )}
          </div>

          {/* Spoljni nalaz stoji uz naše: mikrobiologija, gluten i ostatak heksana se po pravilu rade
              van kuće, pa bez ovoga ekran o lotu kaže manje nego što se o njemu zna. */}
          <ExternalAnalysesPanel subject={{ kind: "lot", id: lot.id }} />

          {arhiv && <ArhivZaLot context={arhiv} lang={lang} />}
        </>
      )}
    </div>
  );
}

/**
 * Šta o ovoj šarži zna arhiv laboratorije.
 *
 * Ekran bez ovoga odgovara „za ovaj LOT još nema nalaza" i kad o proizvodu postoji tri stotine
 * ranijih. Samo 16.185 od 62.078 analiza u arhivu uopšte nosi oznaku lota, pa je šarža bez svog
 * nalaza uobičajen slučaj, a ne znak da se ništa ne zna.
 */
function ArhivZaLot({ context, lang }: { context: BatchArchiveContext; lang: string }) {
  const { t } = useTranslation();
  const n = (v: number) => v.toLocaleString(lang);

  const naLotu = context.analyses.length > 0;
  const naArtiklu = context.articleAnalyses > 0;
  if (!naLotu && !naArtiklu) return null;

  return (
    <div className="card">
      <header className="card-h">
        <h3>{t("readings.archive.title", "Iz arhiva laboratorije")}</h3>
        <span className="muted">
          {naLotu
            ? t("readings.archive.onLot", "{{n}} analiza na ovoj šarži", { n: n(context.analyses.length) })
            : t("readings.archive.notOnLot", "na ovoj šarži nema nalaza")}
        </span>
      </header>

      {naLotu ? (
        <div className="card-b tight">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("lab.col.date", "Datum")}</th>
                <th>{t("lab.col.point", "Kontrolna tačka")}</th>
                <th>{t("lab.col.sampler", "Uzorkivač")}</th>
                <th style={{ textAlign: "right" }}>{t("lab.col.tests", "Testova")}</th>
                <th style={{ textAlign: "right" }}>{t("lab.col.measurements", "Merenja")}</th>
              </tr>
            </thead>
            <tbody>
              {context.analyses.map((a) => (
                <tr key={a.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{formatDate(a.analysedOn, lang)}</td>
                  <td>
                    {a.controlPointCode
                      ? localizedName({ name: a.controlPointCode, nameI18n: a.controlPointName }, lang)
                      : (missingPointLabel(t, a) ?? "—")}
                  </td>
                  <td>{a.samplerName ?? "—"}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{a.tests}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{a.measurements}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-b">
          <p style={{ margin: "0 0 10px" }}>
            {t(
              "readings.archive.articleLead",
              "Na ovoj šarži nalaza nema, ali artikal „{{artikal}}” ima {{n}} analiza u arhivu, od {{od}} do {{do}}.",
              {
                artikal: context.productName ?? "—",
                n: n(context.articleAnalyses),
                od: context.articleFirstOn ? formatDate(context.articleFirstOn, lang) : "—",
                do: context.articleLastOn ? formatDate(context.articleLastOn, lang) : "—",
              },
            )}
          </p>
          {/* Brojka bez puta do nje je slepa ulica: odavde se otvara arhiv, filtriran na taj artikal. */}
          {context.erpProductId && (
            <p style={{ margin: "0 0 10px" }}>
              <Link
                className="btn sm"
                to={`/quality/lab-archive?erpProductId=${encodeURIComponent(context.erpProductId)}${
                  context.productName ? `&naziv=${encodeURIComponent(context.productName)}` : ""
                }`}
              >
                {t("readings.archive.openArticle", "Otvori sve analize ovog artikla")}
              </Link>
            </p>
          )}
          {context.articleParameters.length > 0 && (
            <>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                {t("readings.archive.whatIsMeasured", "Šta se na ovom artiklu meri")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {context.articleParameters.slice(0, 12).map((p) => (
                  <span key={p.parameterId} className="tag neutral">
                    {localizedName({ name: p.key, nameI18n: p.nameI18n }, lang)}
                    <span className="muted" style={{ marginLeft: 6 }}>{n(p.measurements)}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Pokrivenost plana i potpis šefa laboratorije na nju (KK29).
 *
 * Razlika koju ekran mora da pokaže: pokrivenost se računa svaki put kad se pogleda, a potpis je tvrdnja
 * o jednom trenutku — „na dan X, za ovaj LOT i ovu fazu, propisano je bilo N i izvedeno M". Zato se
 * potpisani brojevi prikazuju onakvi kakvi su upisani, a ne preračunati: nalaz unet posle potpisa ne menja
 * potpis, nego se vidi kao razlika između potpisanog i sadašnjeg stanja.
 */
function CoverageTable({
  view,
  lang,
  lotId,
  canSign,
  onSigned,
}: {
  view: CoverageView;
  lang: string;
  lotId: string;
  canSign: boolean;
  onSigned: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [obrazlozenje, setObrazlozenje] = useState("");
  const [busy, setBusy] = useState(false);

  const palletsKnown = view.pallets != null;
  const bezGranice = view.rows.filter((r) => r.limitMissing).length;
  const poPaleti = view.rows.filter((r) => r.frequencyBasis === "PER_PALLET").length;
  // Broj paleta je deo potpisa, ne podešavanje prikaza: „na svaku drugu paletu" propisuje šest nalaza na
  // dvanaest paleta i jedan na dve, pa bi se bez njega potpisao broj manji od propisanog.
  const trebaPalete =
    !palletsKnown &&
    view.rows.some((r) => r.mandatory && r.frequencyBasis === "PER_PALLET");
  // Stavke koje portal ne ume da oceni ni kad je sve uneto: opisna provera nema šta da se izmeri brojem,
  // a nalaz spoljne laboratorije je dokument bez veze sa propisanom stavkom. Nad njima potpis stoji na
  // reči šefa laboratorije — zato obrazloženje nije formalnost.
  const nijeOcenjivo = view.rows.filter((r) => r.mandatory && (r.description != null || r.external)).length;
  const trebaObrazlozenje = !view.complete;
  const smeSada =
    canSign && view.rows.length > 0 && !trebaPalete && (!trebaObrazlozenje || obrazlozenje.trim() !== "");

  async function potpisi() {
    setBusy(true);
    try {
      await qualityReadingApi.signCoverage(lotId, {
        planId: view.planId,
        phase: view.phase,
        pallets: view.pallets,
        note: obrazlozenje.trim() || null,
      });
      setObrazlozenje("");
      await onSigned();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <header className="card-h">
        <h3>{t("readings.coverage", "Pokrivenost plana")}</h3>
        <span className="muted">
          {t("coverage.totals", "portal drži {{izvedeno}} od {{propisano}} propisanih", {
            izvedeno: view.doneTotal,
            propisano: view.requiredTotal,
          })}
        </span>
      </header>

      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 0 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {view.signature ? (
            <>
              <span className="tag ok">{t("coverage.signed", "potpisano")}</span>
              <span style={tiho}>
                {t("coverage.signedBy", "potpisao {{ko}}, {{kada}}", {
                  ko: view.signature.signedBy,
                  kada: formatDateTime(view.signature.signedAt, lang),
                })}
              </span>
              <span style={tiho}>
                {t("coverage.signedNumbers", "tada propisano {{propisano}}, izvedeno {{izvedeno}}", {
                  propisano: view.signature.requiredCount,
                  izvedeno: view.signature.doneCount,
                })}
              </span>
              {view.signature.pallets != null && (
                <span style={tiho}>
                  {t("coverage.signedPallets", "na {{n}} paleta", { n: view.signature.pallets })}
                </span>
              )}
            </>
          ) : (
            <span className="tag neutral">{t("coverage.notSigned", "nije potpisano")}</span>
          )}
        </div>

        {view.signature?.note && (
          <div style={tiho}>
            {t("coverage.signedNote", "obrazloženje potpisa: {{tekst}}", { tekst: view.signature.note })}
          </div>
        )}

        {view.signatureOutdated && (
          <span className="tag" style={znakUpozorenje}>
            {t(
              "coverage.outdated",
              "Posle potpisa se stanje promenilo — sada je propisano {{propisano}}, a izvedeno {{izvedeno}}. Potpis važi za stanje od tada.",
              { propisano: view.requiredTotal, izvedeno: view.doneTotal },
            )}
          </span>
        )}

        {canSign && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 280px" }}>
                <div style={{ ...tiho, marginBottom: 4 }}>
                  {trebaObrazlozenje
                    ? t("coverage.noteRequired", "Obrazloženje — obavezno, pokrivenost nije puna")
                    : t("coverage.noteOptional", "Obrazloženje, ako ga ima")}
                </div>
                <input
                  className="input"
                  value={obrazlozenje}
                  onChange={(e) => setObrazlozenje(e.target.value)}
                  placeholder={t("coverage.notePlaceholder", "šta manjka i na osnovu čega se potpisuje")}
                />
              </div>
              <button
                className="btn primary"
                type="button"
                disabled={busy || !smeSada}
                onClick={() => void potpisi()}
              >
                {view.signature
                  ? t("coverage.signAgain", "Potpiši ponovo")
                  : t("coverage.sign", "Potpiši pokrivenost")}
              </button>
            </div>
            {trebaPalete && (
              <span style={tiho}>
                {t(
                  "coverage.palletsBeforeSign",
                  "Broj paleta je deo potpisa: bez njega se stavke po paleti ne mogu oceniti, pa se pokrivenost ne može potpisati.",
                )}
              </span>
            )}
            {nijeOcenjivo > 0 && (
              <span style={tiho}>
                {t(
                  "coverage.cannotJudgeN",
                  "{{n}} propisanih stavki portal ne može da oceni — spoljna laboratorija ili opisna provera. Potpis nad njima stoji na vašoj reči, pa ide uz obrazloženje.",
                  { n: nijeOcenjivo },
                )}
              </span>
            )}
            {view.signature && (
              <span style={tiho}>
                {t(
                  "coverage.signAgainHint",
                  "Ponovni potpis ne menja prethodni — upisuje se novi, sa brojevima kakvi su sada.",
                )}
              </span>
            )}
          </>
        )}
      </div>
      {(bezGranice > 0 || (!palletsKnown && poPaleti > 0)) && (
        <div className="card-b" style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingBottom: 0 }}>
          {!palletsKnown && poPaleti > 0 && (
            <span className="tag" style={znakUpozorenje}>
              {t(
                "readings.palletsNeeded",
                "{{n}} stavki se meri po paleti, a broj paleta nije upisan — bez njega se te stavke ne mogu oceniti.",
                { n: poPaleti },
              )}
            </span>
          )}
          {bezGranice > 0 && (
            <span className="tag" style={znakUpozorenje}>
              {t("readings.limitsMissingN", "Na {{n}} stavki granica nije uneta, pa se nalaz ni po čemu ne ocenjuje.", {
                n: bezGranice,
              })}
            </span>
          )}
        </div>
      )}
      {view.rows.length === 0 ? (
        <div className="empty">
          {t("coverage.nothingPrescribedPhase", "Plan u ovoj fazi ne propisuje nijedan test, pa nema šta da se potpiše.")}
        </div>
      ) : (
      <div className="card-b tight">
      <table className="tbl">
        <thead>
          <tr>
            <th>{t("quality.plan.col.test", "Test")}</th>
            <th>{t("quality.plan.col.frequency", "Učestalost")}</th>
            <th>{t("readings.col.required", "Propisano")}</th>
            <th>{t("readings.col.done", "Izvedeno")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {view.rows.map((r) => (
            <tr key={r.planItemId}>
              <td>
                <div className="uz-oznake">
                  <span>
                    {r.description ??
                      localizedName({ name: r.parameterKey ?? "", nameI18n: r.parameterNameI18n }, lang)}
                  </span>
                  {!r.mandatory && <span className="tag">{t("quality.plan.optional", "nije obavezan")}</span>}
                  {r.external && <span className="tag">{t("quality.plan.external", "spoljna laboratorija")}</span>}
                  {r.limitMissing && (
                    <span className="tag" style={znakUpozorenje}>
                      {t("quality.plan.noLimit", "nije uneta")}
                    </span>
                  )}
                </div>
              </td>
              <td style={tiho}>{frequencyLabel(t, r.frequencyBasis, r.frequencyCount)}</td>
              <td style={tiho}>{r.required === 0 ? "—" : r.required}</td>
              <td style={tiho}>{r.done}</td>
              <td style={{ textAlign: "right" }}>
                {r.required === 0 ? (
                  <span style={tiho}>{t("readings.cannotJudge", "ne može se oceniti")}</span>
                ) : r.complete ? (
                  <span style={tiho}>{t("readings.complete", "pokriveno")}</span>
                ) : (
                  <span className="tag" style={znakUpozorenje}>
                    {t("readings.missingN", "manjka {{n}}", { n: r.required - Number(r.done) })}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}
    </div>
  );
}

function ReadingTable({
  readings,
  lang,
  onChanged,
  canSign,
}: {
  readings: QualityReading[];
  lang: string;
  onChanged: () => Promise<void>;
  /**
   * Potpis je šefov, i to po pravilu 11. Laborant nalaz unosi i vidi da čeka potpis, ali dugme ne
   * dobija: server bi mu ga odbio, a dugme koje ne može da uspe uči da se odbijanje čita kao kvar.
   * Ista odluka stoji i na ekranu „Čeka potpis", pa dva ekrana istog posla ne smeju da se razilaze.
   */
  canSign: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  async function verify(r: QualityReading) {
    setBusy(r.id);
    try {
      await qualityReadingApi.verify(r.id);
      await onChanged();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>{t("quality.plan.col.test", "Test")}</th>
          <th>{t("readings.col.value", "Vrednost")}</th>
          <th>{t("readings.col.sample", "Uzorak")}</th>
          <th>{t("readings.col.when", "Izmereno")}</th>
          <th>{t("readings.col.who", "Uneo")}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {readings.map((r) => (
          <tr key={r.id}>
            <td>
              {localizedName({ name: r.parameterKey, nameI18n: r.parameterNameI18n }, lang)}
              <span style={{ ...tiho, fontSize: 11.5 }}> {phaseLabel(t, r.phase)}</span>
            </td>
            <td>
              <div className="uz-oznake">
                <strong>{r.displayValue}</strong>
                {r.unit ? <span style={tiho}>{r.unit}</span> : null}
                {r.outOfRange && (
                  <span className="tag" style={znakUpozorenje}>
                    {t("readings.outOfRange", "van granice")}
                  </span>
                )}
              </div>
            </td>
            <td style={tiho}>
              {r.palletNo != null ? t("readings.pallet", "paleta {{n}}", { n: r.palletNo }) : ""}
              {r.sampleLabel ? ` ${r.sampleLabel}` : ""}
              {r.shiftLabel ? ` · ${r.shiftLabel}` : ""}
              {r.palletNo == null && !r.sampleLabel && !r.shiftLabel ? "—" : ""}
            </td>
            <td style={tiho}>{formatDate(r.measuredAt)}</td>
            <td style={tiho}>{r.recordedBy}</td>
            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              {r.status === "PENDING_VERIFICATION" ? (
                canSign ? (
                  <button className="btn sm primary" type="button" disabled={busy === r.id} onClick={() => void verify(r)}>
                    {t("readings.verify", "Potpiši")}
                  </button>
                ) : (
                  <span style={tiho}>{t("readings.awaitingSignature", "čeka potpis šefa kvaliteta")}</span>
                )
              ) : r.status === "VERIFIED" ? (
                <span style={tiho}>
                  {t("readings.verifiedBy", "potpisao {{ko}}", { ko: r.verifiedBy ?? "—" })}
                </span>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Radni list: jedan uzorak, sve što plan propisuje u toj fazi, jedno čuvanje.
 *
 * Laborant nema jedan rezultat nego uzorak pred sobom i spisak onoga što se na njemu meri. Taj spisak
 * nije ovde napisan: dolazi iz plana, pa je forma tačno ono što je propisano, bez ijednog opisa koji bi
 * se mogao razići sa njim.
 *
 * Prazno polje se ne upisuje. Test koji nije izmeren ostaje nemeren, i to se vidi u pokrivenosti —
 * nula upisana da bi se red popunio bila bi izmišljen nalaz.
 */
function ReadingForm({
  lotId,
  phase,
  plan,
  lang,
  onDone,
  onCancel,
}: {
  lotId: string;
  phase: QualityPhase;
  plan: TestPlan;
  lang: string;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  // Nude se samo testovi koje plan propisuje u toj fazi, i samo merljivi — pregled se ne upisuje brojem.
  const stavke = plan.items.filter((i) => i.phase === phase && i.parameterId);

  const [vrednosti, setVrednosti] = useState<Record<string, string>>({});
  const [daNe, setDaNe] = useState<Record<string, "da" | "ne">>({});
  const [palletNo, setPalletNo] = useState("");
  const [sampleLabel, setSampleLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const popunjeno = stavke.filter((i) => {
    const tip = i.resultType ?? "NUMERIC";
    if (tip === "BOOLEAN") return daNe[i.id] !== undefined;
    return (vrednosti[i.id] ?? "").trim() !== "";
  });

  async function submit() {
    if (popunjeno.length === 0) return;
    setBusy(true);
    try {
      await qualityReadingApi.recordSheetForLot(lotId, {
        phase,
        palletNo: palletNo.trim() ? Number(palletNo) : null,
        sampleLabel: sampleLabel.trim() || null,
        note: note.trim() || null,
        values: popunjeno.map((i) => {
          const tip = i.resultType ?? "NUMERIC";
          return {
            parameterId: i.parameterId!,
            planItemId: i.id,
            value: tip === "NUMERIC" ? Number((vrednosti[i.id] ?? "").replace(",", ".")) : null,
            valueBool: tip === "BOOLEAN" ? daNe[i.id] === "da" : null,
            valueText: tip === "TEXT" ? (vrednosti[i.id] ?? "").trim() || null : null,
          };
        }),
      });
      await onDone();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  if (stavke.length === 0) {
    return (
      <div className="card">
        <div className="card-b">
          <p style={tiho}>
            {t(
              "readings.nothingPrescribed",
              "Plan u ovoj fazi ne propisuje nijedan merljiv test. Dodajte ga u konfiguratoru.",
            )}
          </p>
          <button className="btn" type="button" onClick={onCancel}>
            {t("common.cancel", "Otkaži")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <header className="card-h">
        <h3>{t("readings.sheet.title", "Radni list")}</h3>
        <span className="muted">
          {t("readings.sheet.prescribed", "{{n}} propisanih testova u ovoj fazi", { n: stavke.length })}
        </span>
      </header>
      <div className="card-b">
        <div style={mreza}>
          <div>
            <div style={{ ...tiho, marginBottom: 4 }}>{t("readings.palletNo", "Paleta")}</div>
            <input className="input" value={palletNo} onChange={(e) => setPalletNo(e.target.value)} />
          </div>
          <div>
            <div style={{ ...tiho, marginBottom: 4 }}>{t("readings.sampleLabel", "Oznaka uzorka")}</div>
            <input className="input" value={sampleLabel} onChange={(e) => setSampleLabel(e.target.value)} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ ...tiho, marginBottom: 4 }}>{t("quality.plan.field.note", "Napomena")}</div>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <table className="tbl" style={{ marginTop: 14 }}>
          <thead>
            <tr>
              <th>{t("quality.plan.col.test", "Test")}</th>
              <th style={{ width: 200 }}>{t("readings.col.value", "Vrednost")}</th>
              <th>{t("quality.plan.col.limits", "Granice")}</th>
            </tr>
          </thead>
          <tbody>
            {stavke.map((i) => {
              const tip = i.resultType ?? "NUMERIC";
              return (
                <tr key={i.id}>
                  <td>
                    {localizedName({ name: i.parameterKey ?? "", nameI18n: i.parameterNameI18n }, lang)}
                    {i.unit && <span style={tiho}> {i.unit}</span>}
                  </td>
                  <td>
                    {tip === "BOOLEAN" ? (
                      <Select
                        value={daNe[i.id] ?? ""}
                        onChange={(v) => setDaNe((p) => ({ ...p, [i.id]: v as "da" | "ne" }))}
                        options={[
                          { value: "", label: t("readings.sheet.notMeasured", "nije mereno") },
                          { value: "da", label: t("common.yes", "Da") },
                          { value: "ne", label: t("common.no", "Ne") },
                        ]}
                      />
                    ) : (
                      <input
                        className="input"
                        value={vrednosti[i.id] ?? ""}
                        placeholder={t("readings.sheet.notMeasured", "nije mereno")}
                        onChange={(e) => setVrednosti((p) => ({ ...p, [i.id]: e.target.value }))}
                      />
                    )}
                  </td>
                  <td style={tiho}>
                    {i.limitMissing
                      ? t("readings.noLimitShort", "nije uneta, ne ocenjuje se")
                      : granicaTekst(i.acceptMin, i.acceptMax, i.acceptBool, t)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p style={{ ...tiho, marginTop: 10 }}>
          {t(
            "readings.sheet.note",
            "Prazno polje se ne upisuje — test koji nije izmeren ostaje nemeren i vidi se u pokrivenosti. Vrednost van granice čeka potpis šefa laboratorije.",
          )}
        </p>

        <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>
            {t("common.cancel", "Otkaži")}
          </button>
          <button
            className="btn primary"
            type="button"
            disabled={busy || popunjeno.length === 0}
            onClick={() => void submit()}
          >
            {t("readings.sheet.save", "Sačuvaj {{n}} nalaza", { n: popunjeno.length })}
          </button>
        </div>
      </div>
    </div>
  );
}

function granicaTekst(min: number | null, max: number | null, strana: boolean | null, t: TFunction): string {
  // Da-ne test ima granicu bez broja; laborant mora da vidi koja je strana prihvaćena.
  if (strana != null) {
    return strana
      ? t("quality.plan.acceptBool.positive", "pozitivan (prisustvo)")
      : t("quality.plan.acceptBool.negative", "negativan (odsustvo)");
  }
  if (min != null && max != null) return `${min} – ${max}`;
  if (min != null) return `min ${min}`;
  if (max != null) return `max ${max}`;
  return "—";
}

/* ------------------------------------------------------------------ stil */

/*
 * Ekran stoji na sistemu: `card`, `card-h`, `card-b`, `tbl`, `tag`, `empty`, `toolbar`. Ovde je samo
 * ono čega u sistemu nema — boja upozorenja na znački, i mreža polja u obrascu.
 */

const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5 };

const znakUpozorenje: CSSProperties = {
  borderColor: "var(--warn)",
  color: "var(--warn)",
  background: "transparent",
};

const mreza: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 12,
};
